import { authStore } from "@/lib/authStore";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Users, Activity, DollarSign, MessageSquare, Shield, Search,
  ChevronLeft, ChevronRight, Trash2, Ban, CheckCircle2, Pencil,
  BarChart3, FileText, Sun, Moon, RefreshCw, AlertTriangle,
  ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, Eye, X, Loader2,
  HelpCircle, ThumbsUp, ThumbsDown, Tag, Gauge, Sparkles, Repeat,
  Wallet, IndianRupee, Database, ClipboardList, UserCheck, UserX
} from 'lucide-react';
import IndexingJobsTab from '@/pages/admin/IndexingJobsTab.js';
import CorpusHealthTab from '@/pages/admin/CorpusHealthTab.js';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function getAuthHeaders() {
  const token = authStore.getAccessToken();
  return { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' };
}

// ==================== STAT CARD ====================
const StatCard = function({ icon: Icon, label, value, sub, trend, color }) {
  const colorMap = {
    blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    green: 'bg-green-500/10 text-green-600 dark:text-green-400',
    orange: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    red: 'bg-red-500/10 text-red-600 dark:text-red-400',
    purple: 'bg-primary/10 text-primary'
  };
  return (
    <div className="rounded-xl border border-border/50 bg-card p-5 flex items-start gap-4" data-testid={'stat-' + label.toLowerCase().replace(/\s/g, '-')}>
      <div className={'h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ' + (colorMap[color] || colorMap.blue)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold mt-0.5 tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {trend && (
        <div className={'flex items-center gap-0.5 text-xs font-medium px-2 py-1 rounded-full ' + (trend > 0 ? 'text-green-600 bg-green-500/10' : 'text-red-600 bg-red-500/10')}>
          {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
  );
};

// ==================== USER ROW ====================
const UserRow = function({ user, onThrottle, onUnthrottle, onDelete, onChangePlan }) {
  const usage = user.today_usage || {};
  const isThrottled = usage.throttled;
  
  return (
    <tr className="border-b border-border/30 hover:bg-muted/30 transition-colors" data-testid={'user-row-' + user.id}>
      <td className="px-4 py-3">
        <div>
          <p className="text-sm font-medium">{user.name || 'N/A'}</p>
          <p className="text-xs text-muted-foreground">{user.email}</p>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + 
          (user.subscription_plan === 'free' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
           user.subscription_plan === 'yearly' ? 'bg-primary/10 text-primary' :
           'bg-blue-500/10 text-blue-600 dark:text-blue-400')
        }>
          {user.subscription_plan || 'free'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm tabular-nums">{usage.ai_requests || 0}</span>
          <span className="text-xs text-muted-foreground">/ {user.messages_limit || 5}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm tabular-nums">${(usage.estimated_cost_usd || 0).toFixed(4)}</span>
      </td>
      <td className="px-4 py-3">
        {isThrottled ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-600">
            <Ban className="h-3 w-3" /> Throttled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600">
            <CheckCircle2 className="h-3 w-3" /> Active
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-muted-foreground">{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <select
            defaultValue={user.subscription_plan}
            onChange={function(e) { onChangePlan(user.id, e.target.value); }}
            className="text-xs bg-transparent border border-border/50 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary/50"
            data-testid={'plan-select-' + user.id}
          >
            <option value="free">Free</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
          {isThrottled ? (
            <button
              onClick={function() { onUnthrottle(user.id); }}
              className="p-1.5 rounded-lg hover:bg-green-500/10 text-green-600 transition-colors"
              title="Unthrottle"
              data-testid={'unthrottle-' + user.id}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              onClick={function() { onThrottle(user.id); }}
              className="p-1.5 rounded-lg hover:bg-orange-500/10 text-orange-600 transition-colors"
              title="Throttle"
              data-testid={'throttle-' + user.id}
            >
              <Ban className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            onClick={function() { onDelete(user.id, user.email); }}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-600 transition-colors"
            title="Delete"
            data-testid={'delete-user-' + user.id}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ==================== AUDIT ROW ====================
const AuditRow = function({ event }) {
  return (
    <tr className="border-b border-border/30 hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <span className="text-xs text-muted-foreground">{event.timestamp ? new Date(event.timestamp).toLocaleString() : 'N/A'}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm">{event.user_email}</span>
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-secondary">{event.event_type}</span>
      </td>
      <td className="px-4 py-3 tabular-nums text-sm">{event.input_tokens || 0}</td>
      <td className="px-4 py-3 tabular-nums text-sm">{event.output_tokens || 0}</td>
      <td className="px-4 py-3 tabular-nums text-sm">${(event.cost_usd || 0).toFixed(6)}</td>
      <td className="px-4 py-3">
        <span className="text-xs text-muted-foreground">{event.model || 'gpt-5.2'}</span>
      </td>
    </tr>
  );
};

// ==================== MINI BAR CHART ====================
const MiniBarChart = function({ data, maxVal, label }) {
  if (!data || data.length === 0) return null;
  var max = maxVal || Math.max.apply(null, data.map(function(d) { return d.value; })) || 1;
  
  var bars = [];
  for (var i = 0; i < data.length; i++) {
    var pct = Math.max(2, (data[i].value / max) * 100);
    bars.push(
      <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
        <div className="w-full bg-muted/50 rounded-t-sm relative overflow-hidden" style={{ height: '80px' }}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-primary/70 rounded-t-sm transition-all duration-300 group-hover:bg-primary"
            style={{ height: pct + '%' }}
          />
        </div>
        <span className="text-[9px] text-muted-foreground">{data[i].label}</span>
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover border border-border px-2 py-1 rounded text-xs shadow-lg whitespace-nowrap z-10">
          {data[i].value.toFixed(label === '$' ? 4 : 0)}{label === '$' ? ' USD' : ' req'}
        </div>
      </div>
    );
  }
  
  return <div className="flex items-end gap-1">{bars}</div>;
};

// ==================== MAIN ADMIN PAGE ====================
const AdminPage = function() {
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [gapsWindow, setGapsWindow] = useState('7d');
  const [gapsData, setGapsData] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [telemetry, setTelemetry] = useState(null);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [improvements, setImprovements] = useState(null);
  const [costUsage, setCostUsage] = useState(null);
  const [costUsageLoading, setCostUsageLoading] = useState(false);
  const [margin, setMargin] = useState(null);
  const [marginLoading, setMarginLoading] = useState(false);
  const [accessRequests, setAccessRequests] = useState([]);
  const [accessRequestsLoading, setAccessRequestsLoading] = useState(false);
  const [accessRequestsFilter, setAccessRequestsFilter] = useState('pending');

  const PAGE_SIZE = 20;

  const fetchStats = useCallback(async function() {
    try {
      const res = await fetch(API_URL + '/admin/stats', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return await res.json();
    } catch (e) {
      return null;
    }
  }, []);

  const fetchUsers = useCallback(async function(page, search, plan) {
    try {
      var url = API_URL + '/admin/users?skip=' + (page * PAGE_SIZE) + '&limit=' + PAGE_SIZE;
      if (search) url += '&search=' + encodeURIComponent(search);
      if (plan) url += '&plan=' + encodeURIComponent(plan);
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch users');
      return await res.json();
    } catch (e) {
      return { users: [], total: 0 };
    }
  }, []);

  const fetchAuditLog = useCallback(async function(page) {
    try {
      const res = await fetch(API_URL + '/admin/audit-log?skip=' + (page * PAGE_SIZE) + '&limit=' + PAGE_SIZE, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch audit log');
      return await res.json();
    } catch (e) {
      return { events: [], total: 0 };
    }
  }, []);

  const fetchUsageHistory = useCallback(async function() {
    try {
      const res = await fetch(API_URL + '/admin/usage-history?days=7', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      return await res.json();
    } catch (e) {
      return [];
    }
  }, []);

  const loadAll = useCallback(async function() {
    setLoading(true);
    const [s, u, a, h] = await Promise.all([
      fetchStats(),
      fetchUsers(0, '', ''),
      fetchAuditLog(0),
      fetchUsageHistory()
    ]);
    if (s) setStats(s);
    if (u) { setUsers(u.users); setUsersTotal(u.total); }
    if (a) { setAuditLog(a.events); setAuditTotal(a.total); }
    if (h) setUsageHistory(h);
    setLoading(false);
  }, [fetchStats, fetchUsers, fetchAuditLog, fetchUsageHistory]);

  useEffect(function() {
    if (!user || !user.is_admin) {
      navigate('/chat');
      return;
    }
    loadAll();
  }, [user, navigate, loadAll]);

  const handleRefresh = async function() {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleSearchUsers = async function() {
    setUsersPage(0);
    const data = await fetchUsers(0, searchQuery, planFilter);
    setUsers(data.users);
    setUsersTotal(data.total);
  };

  const handleUsersPageChange = async function(newPage) {
    setUsersPage(newPage);
    const data = await fetchUsers(newPage, searchQuery, planFilter);
    setUsers(data.users);
    setUsersTotal(data.total);
  };

  const handleAuditPageChange = async function(newPage) {
    setAuditPage(newPage);
    const data = await fetchAuditLog(newPage);
    setAuditLog(data.events);
    setAuditTotal(data.total);
  };

  const handleThrottle = async function(userId) {
    try {
      await fetch(API_URL + '/admin/users/' + userId + '/throttle?reason=admin_override', {
        method: 'POST', headers: getAuthHeaders()
      });
      toast.success('User throttled');
      handleSearchUsers();
    } catch (e) { toast.error('Failed to throttle user'); }
  };

  const handleUnthrottle = async function(userId) {
    try {
      await fetch(API_URL + '/admin/users/' + userId + '/unthrottle', {
        method: 'POST', headers: getAuthHeaders()
      });
      toast.success('User unthrottled');
      handleSearchUsers();
    } catch (e) { toast.error('Failed to unthrottle user'); }
  };

  const handleDelete = async function(userId, email) {
    if (!window.confirm('Delete user ' + email + '? This cannot be undone.')) return;
    try {
      await fetch(API_URL + '/admin/users/' + userId, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      toast.success('User deleted');
      handleSearchUsers();
      const s = await fetchStats();
      if (s) setStats(s);
    } catch (e) { toast.error('Failed to delete user'); }
  };

  const fetchKnowledgeGaps = useCallback(async function(win) {
    setGapsLoading(true);
    try {
      const res = await fetch(API_URL + '/admin/rag/knowledge-gaps?window=' + win + '&limit=20', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch knowledge gaps');
      const data = await res.json();
      setGapsData(data);
    } catch (e) {
      setGapsData({ clusters: [], feedback_summary: { total: 0, up: 0, down: 0, down_reasons: [] } });
    }
    setGapsLoading(false);
  }, []);

  const fetchGapDigest = useCallback(async function() {
    setDigestLoading(true);
    try {
      const res = await fetch(API_URL + '/admin/rag/gap-digest', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch digest');
      const data = await res.json();
      setDigest(data);
    } catch (e) {
      setDigest(null);
    }
    setDigestLoading(false);
  }, []);

  const fetchTelemetry = useCallback(async function() {
    setTelemetryLoading(true);
    try {
      const res = await fetch(API_URL + '/admin/rag/confidence-telemetry?days=30', { headers: getAuthHeaders() });
      if (res.ok) setTelemetry(await res.json());
    } catch (e) {}
    setTelemetryLoading(false);
  }, []);

  const fetchImprovements = useCallback(async function() {
    try {
      const res = await fetch(API_URL + '/admin/rag/improvements-delivered?days=30', { headers: getAuthHeaders() });
      if (res.ok) setImprovements(await res.json());
    } catch (e) {}
  }, []);

  const fetchCostUsage = useCallback(async function() {
    setCostUsageLoading(true);
    try {
      const res = await fetch(API_URL + '/admin/enterprise/cost-usage?days=30', { headers: getAuthHeaders() });
      if (res.ok) setCostUsage(await res.json());
    } catch (e) {}
    setCostUsageLoading(false);
  }, []);

  const fetchAccessRequests = useCallback(async function(status) {
    setAccessRequestsLoading(true);
    try {
      var url = API_URL + '/admin/access-requests';
      if (status) url += '?status=' + status;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAccessRequests(data.items || []);
      }
    } catch (e) {}
    setAccessRequestsLoading(false);
  }, []);

  const handleApprove = async function(reqId) {
    if (!window.confirm('Approve this request? A free account will be created and login credentials emailed to the applicant automatically.')) return;
    try {
      const res = await fetch(API_URL + '/admin/access-requests/' + reqId + '/approve', {
        method: 'POST', headers: getAuthHeaders()
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed to approve'); return; }
      var emailOk = data.email_delivery && data.email_delivery.status === 'sent';
      var msg = data.user_provisioned
        ? ('Account created. ' + (emailOk ? 'Credentials emailed to applicant.' : 'Warning: email delivery failed — send credentials manually.'))
        : 'Approved (account already existed for this email).';
      emailOk ? toast.success(msg) : toast.warning(msg);
      fetchAccessRequests(accessRequestsFilter);
    } catch (e) { toast.error('Failed to approve request'); }
  };

  const handleReject = async function(reqId) {
    if (!window.confirm('Reject this access request?')) return;
    try {
      const res = await fetch(API_URL + '/admin/access-requests/' + reqId + '/reject', {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({})
      });
      if (!res.ok) { toast.error('Failed to reject'); return; }
      toast.success('Request rejected');
      fetchAccessRequests(accessRequestsFilter);
    } catch (e) { toast.error('Failed to reject request'); }
  };

  const fetchMargin = useCallback(async function() {
    setMarginLoading(true);
    try {
      const res = await fetch(API_URL + '/admin/platform/margin-monitor?days=30', { headers: getAuthHeaders() });
      if (res.ok) setMargin(await res.json());
    } catch (e) {}
    setMarginLoading(false);
  }, []);

  useEffect(function() {
    if (tab === 'knowledge-gaps' && user && user.is_admin) {
      fetchKnowledgeGaps(gapsWindow);
      fetchGapDigest();
    }
    if (tab === 'telemetry' && user && user.is_admin) {
      fetchTelemetry();
    }
    if (tab === 'overview' && user && user.is_admin && !improvements) {
      fetchImprovements();
    }
    if (tab === 'cost-usage' && user && user.is_admin) {
      fetchCostUsage();
    }
    if (tab === 'margin-monitor' && isSuperAdmin) {
      fetchMargin();
    }
    if (tab === 'access-requests' && user && user.is_admin) {
      fetchAccessRequests(accessRequestsFilter);
    }
  }, [tab, gapsWindow, user, isSuperAdmin, accessRequestsFilter, fetchKnowledgeGaps, fetchGapDigest, fetchTelemetry, fetchImprovements, fetchCostUsage, fetchMargin, fetchAccessRequests, improvements]);

  const trackClosureClick = async function(gap) {
    try {
      await fetch(API_URL + '/admin/rag/track-closure-click', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          gap_signature: gap.signature,
          sample_query: gap.sample_query,
          top_vendor: gap.top_vendor,
          top_domain: gap.top_domain,
          suggested_doc_type: gap.suggested_doc_type
        })
      });
    } catch (e) {}
  };

  const handleChangePlan = async function(userId, newPlan) {
    try {
      await fetch(API_URL + '/admin/users/' + userId, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ subscription_plan: newPlan })
      });
      toast.success('Plan updated');
      handleSearchUsers();
    } catch (e) { toast.error('Failed to update plan'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Build usage chart data
  var costChartData = [];
  var reqChartData = [];
  var reversed = usageHistory.slice().reverse();
  for (var ui = 0; ui < reversed.length; ui++) {
    var dayLabel = reversed[ui].date.slice(5);
    costChartData.push({ label: dayLabel, value: reversed[ui].cost_usd });
    reqChartData.push({ label: dayLabel, value: reversed[ui].requests });
  }

  // Build user rows
  var userRows = [];
  for (var uri = 0; uri < users.length; uri++) {
    userRows.push(
      <UserRow
        key={users[uri].id}
        user={users[uri]}
        onThrottle={handleThrottle}
        onUnthrottle={handleUnthrottle}
        onDelete={handleDelete}
        onChangePlan={handleChangePlan}
      />
    );
  }

  // Build audit rows
  var auditRows = [];
  for (var ari = 0; ari < auditLog.length; ari++) {
    auditRows.push(<AuditRow key={ari} event={auditLog[ari]} />);
  }

  // Plan distribution badges
  var planBadges = [];
  if (stats && stats.plan_distribution) {
    for (var pi = 0; pi < stats.plan_distribution.length; pi++) {
      var pd = stats.plan_distribution[pi];
      planBadges.push(
        <div key={pi} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30">
          <span className="text-sm capitalize">{pd.plan || 'free'}</span>
          <span className="text-sm font-bold tabular-nums">{pd.count}</span>
        </div>
      );
    }
  }

  var isSuperAdmin = user && user.is_admin && !user.organization_id;
  var tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'audit', label: 'Audit Log', icon: FileText },
    { id: 'knowledge-gaps', label: 'Knowledge Gaps', icon: HelpCircle },
    { id: 'indexing-jobs', label: 'Indexing Jobs', icon: Database },
    { id: 'corpus-health', label: 'Corpus Health', icon: Activity },
    { id: 'telemetry', label: 'Telemetry', icon: Gauge },
    { id: 'cost-usage', label: 'Cost & Usage', icon: Wallet },
    { id: 'access-requests', label: 'Access Requests', icon: ClipboardList }
  ];
  if (isSuperAdmin) {
    tabs.push({ id: 'margin-monitor', label: 'Margin Monitor', icon: IndianRupee });
  }

  var tabButtons = [];
  for (var ti = 0; ti < tabs.length; ti++) {
    var t = tabs[ti];
    var TabIcon = t.icon;
    tabButtons.push(
      <button
        key={t.id}
        onClick={((tabId) => function() { setTab(tabId); })(t.id)}
        className={'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ' + (tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}
        data-testid={'tab-' + t.id}
      >
        <TabIcon className="h-4 w-4" />
        {t.label}
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/chat" className="flex items-center gap-2">
              <img src="/queryverse-logo.png" alt="QueryVerse" className="h-8" />
            </Link>
            <div className="h-6 w-px bg-border/50" />
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h1 className="font-semibold text-lg">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleRefresh} disabled={refreshing} className="p-2 rounded-lg hover:bg-secondary transition-colors" data-testid="refresh-btn">
              <RefreshCw className={'h-4 w-4 ' + (refreshing ? 'animate-spin' : '')} />
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary transition-colors" data-testid="admin-theme-btn">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/chat">
              <Button variant="outline" size="sm" className="rounded-full" data-testid="back-to-chat-btn">
                <MessageSquare className="h-4 w-4 mr-1.5" /> Chat
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tab Bar */}
        <div className="flex items-center gap-2 mb-6" data-testid="admin-tabs">
          {tabButtons}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-6" data-testid="overview-tab">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={stats ? stats.total_users : 0} sub={stats ? stats.active_today + ' active today' : ''} color="blue" />
              <StatCard icon={MessageSquare} label="Total Conversations" value={stats ? stats.total_conversations : 0} sub={stats ? stats.today_requests + ' requests today' : ''} color="purple" />
              <StatCard icon={DollarSign} label="Today's Cost" value={'$' + (stats ? stats.today_cost_usd.toFixed(4) : '0.0000')} sub={stats && stats.total_revenue_paise ? 'Revenue: Rs ' + (stats.total_revenue_paise / 100).toLocaleString() : 'No revenue yet'} color="green" />
              <StatCard icon={AlertTriangle} label="Throttled Users" value={stats ? stats.throttled_today : 0} sub="Exceeding daily limits" color={stats && stats.throttled_today > 0 ? 'red' : 'green'} />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border/50 bg-card p-5" data-testid="cost-chart">
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  Daily Cost (7-day)
                </h3>
                <MiniBarChart data={costChartData} label="$" />
              </div>
              <div className="rounded-xl border border-border/50 bg-card p-5" data-testid="requests-chart">
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  Daily Requests (7-day)
                </h3>
                <MiniBarChart data={reqChartData} label="#" />
              </div>
            </div>

            {/* Knowledge Improvements Delivered (operational outcome widget) */}
            {improvements && (
              <div className="rounded-xl border border-border/50 bg-card p-5" data-testid="improvements-delivered">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Knowledge Improvements Delivered
                    <span className="text-[10px] text-muted-foreground font-normal">(Last {improvements.window_days} days)</span>
                  </h3>
                  <span className="text-[10px] text-muted-foreground">Outcome-based metric · 2-query confirmation rule</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="rounded-lg bg-muted/30 p-3 text-center">
                    <p className="text-2xl font-bold text-primary tabular-nums" data-testid="kid-gaps-addressed">{improvements.gaps_addressed}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Gaps addressed</p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-3 text-center">
                    <p className="text-2xl font-bold tabular-nums" data-testid="kid-queries-resolved">{improvements.queries_resolved_estimate}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Queries resolved</p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-3 text-center">
                    <p className="text-sm font-bold tabular-nums" data-testid="kid-vendors-improved">
                      {improvements.vendors_improved.length > 0 ? improvements.vendors_improved.join(', ') : '—'}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Vendors improved</p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-3 text-center">
                    <p className="text-sm font-bold tabular-nums" data-testid="kid-domains-improved">
                      {improvements.domains_improved.length > 0 ? improvements.domains_improved.join(', ') : '—'}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Domains improved</p>
                  </div>
                </div>
                {/* Before vs After rates */}
                <div className="grid grid-cols-2 gap-3 text-xs" data-testid="kid-before-after">
                  <div className="rounded-lg border border-border/40 p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Low-confidence rate</p>
                    <div className="flex items-baseline gap-2">
                      <span className="tabular-nums">{improvements.before.low_conf_rate}%</span>
                      <ArrowUpRight className="h-3 w-3 text-muted-foreground rotate-90" />
                      <span className="tabular-nums font-semibold">{improvements.after.low_conf_rate}%</span>
                      {improvements.low_conf_rate_change !== 0 && (
                        <span className={'text-[10px] tabular-nums ' + (improvements.low_conf_rate_change < 0 ? 'text-green-600' : 'text-red-600')}>
                          {improvements.low_conf_rate_change > 0 ? '+' : ''}{improvements.low_conf_rate_change}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-border/40 p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">"Not found" rate</p>
                    <div className="flex items-baseline gap-2">
                      <span className="tabular-nums">{improvements.before.not_found_rate}%</span>
                      <ArrowUpRight className="h-3 w-3 text-muted-foreground rotate-90" />
                      <span className="tabular-nums font-semibold">{improvements.after.not_found_rate}%</span>
                      {improvements.not_found_rate_change !== 0 && (
                        <span className={'text-[10px] tabular-nums ' + (improvements.not_found_rate_change < 0 ? 'text-green-600' : 'text-red-600')}>
                          {improvements.not_found_rate_change > 0 ? '+' : ''}{improvements.not_found_rate_change}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Engagement signal */}
                <div className="mt-3 text-[11px] text-muted-foreground flex flex-wrap gap-3" data-testid="kid-engagement">
                  <span>Deep-link clicks: <span className="text-foreground font-medium">{improvements.engagement.deep_link_clicks}</span></span>
                  <span>Uploads within 72h: <span className="text-foreground font-medium">{improvements.engagement.clicks_with_upload_in_72h}</span></span>
                  <span>Conversion: <span className="text-foreground font-medium">{improvements.engagement.conversion_pct}%</span></span>
                </div>
              </div>
            )}

            {/* Plan Distribution */}
            <div className="rounded-xl border border-border/50 bg-card p-5" data-testid="plan-distribution">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Plan Distribution
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {planBadges.length > 0 ? planBadges : <p className="text-xs text-muted-foreground col-span-5">No data yet</p>}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div className="space-y-4" data-testid="users-tab">
            {/* Search & Filter Bar */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={function(e) { setSearchQuery(e.target.value); }}
                  onKeyDown={function(e) { if (e.key === 'Enter') handleSearchUsers(); }}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-secondary/30 border border-border/50 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="user-search-input"
                />
              </div>
              <select
                value={planFilter}
                onChange={function(e) { setPlanFilter(e.target.value); }}
                className="px-4 py-2.5 rounded-xl bg-secondary/30 border border-border/50 text-sm focus:outline-none focus:border-primary/50"
                data-testid="plan-filter-select"
              >
                <option value="">All Plans</option>
                <option value="free">Free</option>
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
              <Button onClick={handleSearchUsers} size="sm" className="rounded-full" data-testid="search-users-btn">
                <Search className="h-4 w-4 mr-1" /> Search
              </Button>
            </div>

            {/* Users Table */}
            <div className="rounded-xl border border-border/50 overflow-hidden bg-card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Today Queries</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Today Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Joined</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userRows.length > 0 ? userRows : (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No users found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                <span className="text-xs text-muted-foreground">
                  Showing {usersPage * PAGE_SIZE + 1} to {Math.min((usersPage + 1) * PAGE_SIZE, usersTotal)} of {usersTotal}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={function() { handleUsersPageChange(usersPage - 1); }}
                    disabled={usersPage === 0}
                    className="p-1.5 rounded-lg hover:bg-secondary transition-colors disabled:opacity-30"
                    data-testid="users-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-medium">Page {usersPage + 1}</span>
                  <button
                    onClick={function() { handleUsersPageChange(usersPage + 1); }}
                    disabled={(usersPage + 1) * PAGE_SIZE >= usersTotal}
                    className="p-1.5 rounded-lg hover:bg-secondary transition-colors disabled:opacity-30"
                    data-testid="users-next-page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Log Tab */}
        {tab === 'audit' && (
          <div className="space-y-4" data-testid="audit-tab">
            <div className="rounded-xl border border-border/50 overflow-hidden bg-card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Input Tokens</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Output Tokens</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Model</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.length > 0 ? auditRows : (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No audit events found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                <span className="text-xs text-muted-foreground">
                  Showing {auditPage * PAGE_SIZE + 1} to {Math.min((auditPage + 1) * PAGE_SIZE, auditTotal)} of {auditTotal}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={function() { handleAuditPageChange(auditPage - 1); }}
                    disabled={auditPage === 0}
                    className="p-1.5 rounded-lg hover:bg-secondary transition-colors disabled:opacity-30"
                    data-testid="audit-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-medium">Page {auditPage + 1}</span>
                  <button
                    onClick={function() { handleAuditPageChange(auditPage + 1); }}
                    disabled={(auditPage + 1) * PAGE_SIZE >= auditTotal}
                    className="p-1.5 rounded-lg hover:bg-secondary transition-colors disabled:opacity-30"
                    data-testid="audit-next-page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Knowledge Gaps Tab */}
        {tab === 'knowledge-gaps' && (
          <div className="space-y-4" data-testid="knowledge-gaps-tab">
            {/* Weekly Gap Digest Card */}
            {digest && digest.top_gaps && digest.top_gaps.length > 0 && (
              <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5" data-testid="gap-digest-card">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-base font-semibold flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      {digest.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">{digest.summary}</p>
                  </div>
                  {digest.impact_estimate && digest.impact_estimate.percent > 0 && (
                    <div className="flex-shrink-0 text-right">
                      <p className="text-2xl font-bold text-primary tabular-nums">{digest.impact_estimate.percent}%</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Coverable</p>
                    </div>
                  )}
                </div>
                {digest.impact_estimate && digest.impact_estimate.label && (
                  <p className="text-xs text-muted-foreground mb-3" data-testid="digest-impact-label">
                    {digest.impact_estimate.label}
                  </p>
                )}
                <div className="space-y-2">
                  {(function() {
                    var rows = [];
                    for (var gi = 0; gi < digest.top_gaps.length; gi++) {
                      var g = digest.top_gaps[gi];
                      var natureStyle = g.nature === 'true_gap'
                        ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30';
                      var natureLabel = g.nature === 'true_gap' ? 'True content gap' : 'Quality issue';
                      rows.push(
                        <div key={gi} className="rounded-lg border border-border/50 bg-card p-3" data-testid={'digest-gap-' + gi}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ' + natureStyle}>
                                  {natureLabel}
                                </span>
                                {g.recurring && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30" data-testid={'recurring-badge-' + gi}>
                                    <Repeat className="h-3 w-3" /> Recurring gap
                                  </span>
                                )}
                                {g.top_vendor && <span className="px-1.5 py-0.5 rounded bg-secondary/60 text-[10px]">{g.top_vendor}</span>}
                                {g.top_domain && <span className="px-1.5 py-0.5 rounded bg-secondary/60 text-[10px]">{g.top_domain}</span>}
                                <span className="text-[10px] text-muted-foreground">× {g.count}</span>
                              </div>
                              <p className="text-sm font-medium truncate" title={g.sample_query}>{g.sample_query}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                Suggested: <span className="text-foreground font-medium">{g.suggested_doc_type}</span>
                                {' · '}
                                Conf dist H/M/L/N: <span className="tabular-nums">{g.confidence_dist.high}/{g.confidence_dist.medium}/{g.confidence_dist.low}/{g.confidence_dist.none}</span>
                              </p>
                            </div>
                            <Link
                              to={g.action_deep_link}
                              onClick={((gap) => function() { trackClosureClick(gap); })(g)}
                              className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                              data-testid={'close-gap-btn-' + gi}
                            >
                              Close this gap
                              <ArrowUpRight className="h-3 w-3" />
                            </Link>
                          </div>
                          {(g.verification_query || g.verification_query_variant) && (
                            <div className="mt-2 p-2 rounded-md bg-muted/30 border border-border/30 text-[11px]" data-testid={'verification-queries-' + gi}>
                              <p className="text-muted-foreground font-medium mb-1">After uploading, test with:</p>
                              {g.verification_query && (
                                <p className="text-foreground italic">• {g.verification_query}</p>
                              )}
                              {g.verification_query_variant && (
                                <p className="text-foreground italic">• {g.verification_query_variant}</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return rows;
                  })()}
                </div>
              </div>
            )}

            {digestLoading && !digest && (
              <div className="rounded-xl border border-border/50 bg-card p-6 flex items-center justify-center" data-testid="digest-loading">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Window toggle + feedback summary */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2" data-testid="gaps-window-toggle">
                <button
                  onClick={function() { setGapsWindow('7d'); }}
                  className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (gapsWindow === '7d' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/80')}
                  data-testid="gaps-window-7d"
                >
                  Last 7 days
                </button>
                <button
                  onClick={function() { setGapsWindow('all'); }}
                  className={'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ' + (gapsWindow === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/80')}
                  data-testid="gaps-window-all"
                >
                  All time
                </button>
              </div>
              <div className="flex items-center gap-3 text-xs" data-testid="feedback-summary">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/30">
                  <ThumbsUp className="h-3 w-3" /> {gapsData ? gapsData.feedback_summary.up : 0}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30">
                  <ThumbsDown className="h-3 w-3" /> {gapsData ? gapsData.feedback_summary.down : 0}
                </span>
                <span className="text-muted-foreground">
                  {gapsData ? gapsData.feedback_summary.total : 0} total feedback
                </span>
              </div>
            </div>

            {/* Down-reason breakdown */}
            {gapsData && gapsData.feedback_summary.down_reasons && gapsData.feedback_summary.down_reasons.length > 0 && (
              <div className="rounded-xl border border-border/50 bg-card p-4" data-testid="down-reasons">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Thumbs-down reasons</p>
                <div className="flex flex-wrap gap-2">
                  {(function() {
                    var els = [];
                    for (var ri = 0; ri < gapsData.feedback_summary.down_reasons.length; ri++) {
                      var r = gapsData.feedback_summary.down_reasons[ri];
                      els.push(
                        <span key={ri} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted/50 text-xs border border-border/50">
                          <span className="capitalize">{r.reason || 'unspecified'}</span>
                          <span className="font-bold tabular-nums text-primary">{r.count}</span>
                        </span>
                      );
                    }
                    return els;
                  })()}
                </div>
              </div>
            )}

            {/* Clusters table */}
            <div className="rounded-xl border border-border/50 overflow-hidden bg-card">
              <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 text-primary" />
                    Unanswered & Low-Confidence Query Clusters
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {gapsData ? gapsData.total_gap_queries + ' gap queries in window' : 'Loading...'}
                  </p>
                </div>
              </div>

              {gapsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border/50">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Query Cluster</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">7d</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">All-time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Conf H/M/L/N</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Unanswered</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Vendor / Domain</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(function() {
                        var rows = [];
                        var clusters = gapsData ? gapsData.clusters : [];
                        if (!clusters || clusters.length === 0) {
                          rows.push(
                            <tr key="empty">
                              <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                                No knowledge gaps in this window. The KB is covering queries well.
                              </td>
                            </tr>
                          );
                          return rows;
                        }
                        for (var ki = 0; ki < clusters.length; ki++) {
                          var k = clusters[ki];
                          var last = k.last_seen ? new Date(k.last_seen).toLocaleDateString() : '-';
                          var cd = k.confidence_dist || { high: 0, medium: 0, low: 0, none: 0 };
                          rows.push(
                            <tr key={ki} className="border-b border-border/30 hover:bg-muted/20" data-testid={'gap-row-' + ki}>
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium truncate max-w-md" title={k.sample_query}>{k.sample_query}</p>
                                <p className="text-[10px] text-muted-foreground truncate max-w-md">keywords: {k.signature}</p>
                              </td>
                              <td className="px-4 py-3 tabular-nums text-sm font-bold text-primary">{k.count_7d}</td>
                              <td className="px-4 py-3 tabular-nums text-sm">{k.count_all_time}</td>
                              <td className="px-4 py-3 text-xs tabular-nums text-muted-foreground">
                                <span className="text-green-600 dark:text-green-400">{cd.high}</span>
                                {' / '}
                                <span className="text-amber-600 dark:text-amber-400">{cd.medium}</span>
                                {' / '}
                                <span className="text-red-600 dark:text-red-400">{cd.low}</span>
                                {' / '}
                                <span>{cd.none}</span>
                              </td>
                              <td className="px-4 py-3 tabular-nums text-sm">{k.unanswered}</td>
                              <td className="px-4 py-3 text-xs">
                                <div className="flex flex-wrap gap-1">
                                  {k.top_vendor && <span className="px-1.5 py-0.5 rounded bg-secondary/60">{k.top_vendor}</span>}
                                  {k.top_domain && <span className="px-1.5 py-0.5 rounded bg-secondary/60">{k.top_domain}</span>}
                                  {!k.top_vendor && !k.top_domain && <span className="text-muted-foreground">—</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-xs text-muted-foreground">{last}</td>
                            </tr>
                          );
                        }
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Indexing Jobs Tab */}
        {tab === 'indexing-jobs' && <IndexingJobsTab />}

        {tab === 'corpus-health' && <CorpusHealthTab />}

        {/* Telemetry Tab */}
        {tab === 'telemetry' && (
          <div className="space-y-4" data-testid="telemetry-tab">
            {telemetryLoading && !telemetry && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {telemetry && (
              <>
                {/* Summary chips */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="telemetry-summary">
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Low-conf + Thumbs-up</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold tabular-nums" data-testid="telem-low-up-count">{telemetry.low_conf_up.total}</span>
                      {(function() {
                        var t = telemetry.low_conf_up.trend;
                        if (t.direction === 'up') return <span className="inline-flex items-center text-xs text-red-600"><TrendingUp className="h-3 w-3" /> +{t.delta}</span>;
                        if (t.direction === 'down') return <span className="inline-flex items-center text-xs text-green-600"><TrendingDown className="h-3 w-3" /> {t.delta}</span>;
                        return <span className="text-xs text-muted-foreground">flat</span>;
                      })()}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Threshold may be too strict</p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">High-conf + Thumbs-down</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold tabular-nums" data-testid="telem-high-down-count">{telemetry.high_conf_down.total}</span>
                      {(function() {
                        var t = telemetry.high_conf_down.trend;
                        if (t.direction === 'up') return <span className="inline-flex items-center text-xs text-red-600"><TrendingUp className="h-3 w-3" /> +{t.delta}</span>;
                        if (t.direction === 'down') return <span className="inline-flex items-center text-xs text-green-600"><TrendingDown className="h-3 w-3" /> {t.delta}</span>;
                        return <span className="text-xs text-muted-foreground">flat</span>;
                      })()}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Check grounding / conflicting sources</p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">OCR answers</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold tabular-nums" data-testid="telem-ocr-pct">{telemetry.ocr_measurement.answers_with_ocr_pct}%</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {telemetry.ocr_measurement.answers_with_ocr_chunks} / {telemetry.ocr_measurement.answers_total} answers · {telemetry.ocr_measurement.docs_ocr} OCR doc(s)
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Variance vs feedback</p>
                    <div className="flex flex-col gap-0.5 text-xs tabular-nums">
                      <span>ðŸ‘ avg: <span className="font-medium">{telemetry.variance_vs_feedback.avg_variance_on_thumbs_up}</span> (n={telemetry.variance_vs_feedback.sample_up})</span>
                      <span>ðŸ‘Ž avg: <span className="font-medium">{telemetry.variance_vs_feedback.avg_variance_on_thumbs_down}</span> (n={telemetry.variance_vs_feedback.sample_down})</span>
                    </div>
                  </div>
                </div>

                {/* Anomaly tables */}
                {(function() {
                  var sections = [
                    { key: 'low_conf_up', title: 'Low confidence · Thumbs-up (threshold may be too strict)', items: telemetry.low_conf_up.items, tid: 'telemetry-low-up-table' },
                    { key: 'high_conf_down', title: 'High confidence · Thumbs-down (grounding/retrieval issues)', items: telemetry.high_conf_down.items, tid: 'telemetry-high-down-table' }
                  ];
                  var els = [];
                  for (var si = 0; si < sections.length; si++) {
                    var sec = sections[si];
                    var rows = [];
                    if (!sec.items || sec.items.length === 0) {
                      rows.push(<tr key="empty"><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No anomalies in this window.</td></tr>);
                    } else {
                      for (var ii = 0; ii < sec.items.length; ii++) {
                        var it = sec.items[ii];
                        var when = it.created_at ? new Date(it.created_at).toLocaleDateString() : '-';
                        rows.push(
                          <tr key={ii} className="border-b border-border/30 hover:bg-muted/20">
                            <td className="px-4 py-2">
                              <p className="text-sm font-medium truncate max-w-lg" title={it.query}>{it.query}</p>
                              <p className="text-[10px] text-muted-foreground">{it.action_hint}</p>
                            </td>
                            <td className="px-4 py-2 text-xs">
                              <span className="capitalize font-medium">{it.confidence_label}</span>
                              {typeof it.confidence_score === 'number' && <span className="text-muted-foreground"> · {Math.round(it.confidence_score * 100)}%</span>}
                            </td>
                            <td className="px-4 py-2 text-xs">
                              {it.rating === 'up' ? <span className="text-green-600">ðŸ‘</span> : <span className="text-red-600">ðŸ‘Ž</span>}
                              {it.reason && <span className="text-muted-foreground ml-1 capitalize">({it.reason})</span>}
                            </td>
                            <td className="px-4 py-2 text-xs text-muted-foreground">{when}</td>
                          </tr>
                        );
                      }
                    }
                    els.push(
                      <div key={sec.key} className="rounded-xl border border-border/50 bg-card overflow-hidden" data-testid={sec.tid}>
                        <div className="px-4 py-3 border-b border-border/50">
                          <h3 className="text-sm font-semibold">{sec.title}</h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-muted/50 border-b border-border/50">
                                <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Query & Hint</th>
                                <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Confidence</th>
                                <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Feedback</th>
                                <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">When</th>
                              </tr>
                            </thead>
                            <tbody>{rows}</tbody>
                          </table>
                        </div>
                      </div>
                    );
                  }
                  return els;
                })()}
              </>
            )}
          </div>
        )}

        {/* Cost & Usage Tab (enterprise admin) */}
        {tab === 'cost-usage' && (
          <div className="space-y-4" data-testid="cost-usage-tab">
            {costUsageLoading && !costUsage && (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            )}
            {costUsage && (
              <>
                {/* KPI row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cost-usage-kpis">
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Total Cost (30d)</p>
                    <p className="text-2xl font-bold tabular-nums" data-testid="kpi-total-cost">₹{costUsage.total_inr.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      LLM ₹{costUsage.llm_inr} · Storage ₹{costUsage.storage_inr} · OCR ₹{costUsage.ocr_inr}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Queries</p>
                    <p className="text-2xl font-bold tabular-nums" data-testid="kpi-queries">{costUsage.total_queries.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {costUsage.seats.included_queries_total.toLocaleString()} included / {costUsage.plan.name}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Cost per User</p>
                    <p className="text-2xl font-bold tabular-nums" data-testid="kpi-cost-per-user">₹{costUsage.per_user_metrics.cost_per_user_inr}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {costUsage.per_user_metrics.avg_queries_per_user} avg queries / user
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Avg Cost / Query</p>
                    <p className="text-2xl font-bold tabular-nums" data-testid="kpi-avg-cost-per-query">₹{costUsage.per_user_metrics.avg_cost_per_query_inr}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {costUsage.seats.active_last_window} / {costUsage.seats.total} active seats
                    </p>
                  </div>
                </div>

                {/* Plan + Projection band */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-border/50 bg-card p-4" data-testid="plan-usage-card">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold">Current Plan · {costUsage.plan.name}</h3>
                      <span className="text-[11px] text-muted-foreground">₹{costUsage.plan.annual_per_employee_inr.toLocaleString()}/employee/yr</span>
                    </div>
                    {(function() {
                      var pct = costUsage.seats.included_queries_total > 0
                        ? Math.min(100, Math.round((costUsage.total_queries / costUsage.seats.included_queries_total) * 100))
                        : 0;
                      var barCls = pct >= 100 ? 'bg-red-500' : (pct >= 80 ? 'bg-amber-500' : 'bg-primary');
                      return (
                        <>
                          <div className="flex justify-between text-[11px] mb-1">
                            <span>{costUsage.total_queries.toLocaleString()} / {costUsage.seats.included_queries_total.toLocaleString()} queries</span>
                            <span className="font-medium tabular-nums">{pct}%</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden" data-testid="plan-quota-bar">
                            <div className={'h-full transition-all ' + barCls} style={{width: pct + '%'}} />
                          </div>
                        </>
                      );
                    })()}
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Overage: ₹{costUsage.plan.overage_per_query_inr}/query, ₹{costUsage.plan.overage_per_gb_month_inr}/GB-mo
                    </p>
                  </div>

                  <div className="rounded-xl border border-border/50 bg-card p-4" data-testid="projection-card">
                    <h3 className="text-sm font-semibold mb-2">Projection (last 7d rate × remaining days)</h3>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><p className="text-[10px] text-muted-foreground">Daily rate (7d)</p><p className="font-bold tabular-nums">{costUsage.projection.daily_rate_7d}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Projected total</p><p className="font-bold tabular-nums">{costUsage.projection.projected_total_queries.toLocaleString()}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Projected overage</p><p className="font-bold tabular-nums text-amber-600" data-testid="projected-overage-inr">₹{costUsage.projection.projected_overage_inr.toLocaleString()}</p></div>
                    </div>
                  </div>
                </div>

                {/* Top consumers */}
                <div className="rounded-xl border border-border/50 bg-card overflow-hidden" data-testid="top-consumers-table">
                  <div className="px-4 py-3 border-b border-border/50">
                    <h3 className="text-sm font-semibold">Top Consumers (last 30d) — ranked by cost, not just count</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/50 border-b border-border/50">
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Queries</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">% of Queries</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Cost (₹)</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">% of Cost</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Tokens (in/out)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(function() {
                          var rows = [];
                          if (!costUsage.top_consumers || costUsage.top_consumers.length === 0) {
                            rows.push(<tr key="empty"><td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">No query activity yet.</td></tr>);
                          } else {
                            for (var ci = 0; ci < costUsage.top_consumers.length; ci++) {
                              var c = costUsage.top_consumers[ci];
                              rows.push(
                                <tr key={ci} className="border-b border-border/30 hover:bg-muted/20" data-testid={'consumer-row-' + ci}>
                                  <td className="px-4 py-2 text-sm truncate max-w-xs">{c.email}</td>
                                  <td className="px-4 py-2 text-sm tabular-nums">{c.queries.toLocaleString()}</td>
                                  <td className="px-4 py-2 text-xs tabular-nums text-muted-foreground">{c.pct_of_total_queries}%</td>
                                  <td className="px-4 py-2 text-sm tabular-nums font-semibold">₹{c.cost_inr}</td>
                                  <td className="px-4 py-2 text-xs tabular-nums text-primary font-medium">{c.pct_of_total_cost}%</td>
                                  <td className="px-4 py-2 text-xs tabular-nums text-muted-foreground">{c.input_tokens.toLocaleString()} / {c.output_tokens.toLocaleString()}</td>
                                </tr>
                              );
                            }
                          }
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Vendor + Domain breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="breakdowns">
                  {(function() {
                    var panels = [
                      { title: 'By Vendor', rows: costUsage.vendor_breakdown, tid: 'vendor-breakdown' },
                      { title: 'By Domain', rows: costUsage.domain_breakdown, tid: 'domain-breakdown' }
                    ];
                    var els = [];
                    for (var pi = 0; pi < panels.length; pi++) {
                      var p = panels[pi];
                      var rowEls = [];
                      if (!p.rows || p.rows.length === 0) {
                        rowEls.push(<p key="empty" className="text-xs text-muted-foreground p-4">No data.</p>);
                      } else {
                        var total = 0;
                        for (var ri = 0; ri < p.rows.length; ri++) total += p.rows[ri].count;
                        for (var rj = 0; rj < p.rows.length; rj++) {
                          var row = p.rows[rj];
                          var pct = total ? Math.round((row.count / total) * 100) : 0;
                          rowEls.push(
                            <div key={rj} className="flex items-center justify-between gap-2 py-1">
                              <span className="text-xs truncate flex-1">{row.label}</span>
                              <span className="text-xs tabular-nums text-muted-foreground">{row.count}</span>
                              <span className="text-[10px] tabular-nums text-primary w-10 text-right">{pct}%</span>
                            </div>
                          );
                        }
                      }
                      els.push(
                        <div key={p.tid} className="rounded-xl border border-border/50 bg-card p-4" data-testid={p.tid}>
                          <h3 className="text-sm font-semibold mb-2">{p.title}</h3>
                          {rowEls}
                        </div>
                      );
                    }
                    return els;
                  })()}
                </div>
              </>
            )}
          </div>
        )}

        {/* Access Requests Tab */}
        {tab === 'access-requests' && (
          <div className="space-y-4" data-testid="access-requests-tab">
            {/* Filter bar */}
            <div className="flex items-center gap-2">
              {['pending', 'approved', 'rejected'].map(function(s) {
                return (
                  <button
                    key={s}
                    onClick={function() { setAccessRequestsFilter(s); fetchAccessRequests(s); }}
                    className={'px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize ' +
                      (accessRequestsFilter === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/80')}
                  >
                    {s}
                  </button>
                );
              })}
              <button
                onClick={function() { fetchAccessRequests(accessRequestsFilter); }}
                className="ml-auto p-2 rounded-lg hover:bg-secondary transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>

            {accessRequestsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : accessRequests.length === 0 ? (
              <div className="rounded-xl border border-border/50 bg-card p-10 text-center text-sm text-muted-foreground">
                No {accessRequestsFilter} access requests.
              </div>
            ) : (
              <div className="space-y-3">
                {accessRequests.map(function(r) {
                  return (
                    <div key={r.id} className="rounded-xl border border-border/50 bg-card p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="font-semibold text-sm">{r.name}</span>
                            <span className="text-xs text-muted-foreground">{r.business_email}</span>
                            <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ' +
                              (r.status === 'pending' ? 'bg-amber-500/10 text-amber-600' :
                               r.status === 'approved' ? 'bg-green-500/10 text-green-600' :
                               'bg-red-500/10 text-red-600')}>
                              {r.status}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-1 text-xs text-muted-foreground">
                            <span><span className="font-medium text-foreground">Company: </span>{r.company}</span>
                            <span><span className="font-medium text-foreground">Role: </span>{r.role || '—'}</span>
                            <span><span className="font-medium text-foreground">Domain: </span>{r.telecom_domain || '—'}</span>
                            <span><span className="font-medium text-foreground">Country: </span>{r.country || '—'}</span>
                            <span><span className="font-medium text-foreground">Team: </span>{r.team_size || '—'}</span>
                          </div>
                          {r.use_case && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">Use case: </span>{r.use_case}
                            </p>
                          )}
                          <p className="mt-1 text-[10px] text-muted-foreground">
                            Submitted: {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                            {r.reviewed_by && (' · Reviewed by ' + r.reviewed_by + ' on ' + (r.reviewed_at ? new Date(r.reviewed_at).toLocaleString() : '—'))}
                          </p>
                        </div>
                        {r.status === 'pending' && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={function() { handleApprove(r.id); }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-600 hover:bg-green-500/20 text-xs font-medium transition-colors"
                            >
                              <UserCheck className="h-3.5 w-3.5" /> Approve
                            </button>
                            <button
                              onClick={function() { handleReject(r.id); }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-600 hover:bg-red-500/20 text-xs font-medium transition-colors"
                            >
                              <UserX className="h-3.5 w-3.5" /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Margin Monitor Tab (super admin only) */}
        {tab === 'margin-monitor' && isSuperAdmin && (
          <div className="space-y-4" data-testid="margin-monitor-tab">
            {marginLoading && !margin && (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            )}
            {margin && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="margin-totals">
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Total Cost (30d)</p>
                    <p className="text-2xl font-bold tabular-nums">₹{margin.totals.cost_inr.toLocaleString()}</p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Total Revenue</p>
                    <p className="text-2xl font-bold tabular-nums">₹{margin.totals.revenue_inr.toLocaleString()}</p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Cost / Revenue</p>
                    <p className="text-2xl font-bold tabular-nums" data-testid="overall-ratio">{margin.totals.overall_ratio_pct}%</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Warn ≥{margin.thresholds.warning_pct}% · Alert ≥{margin.thresholds.alert_pct}%</p>
                  </div>
                  <div className="rounded-xl border border-border/50 bg-card p-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Tenants</p>
                    <p className="text-2xl font-bold tabular-nums">{margin.totals.tenants_total}</p>
                    <p className="text-[10px] mt-1">
                      <span className="text-amber-600">{margin.totals.tenants_yellow} yellow</span>
                      {' · '}
                      <span className="text-red-600">{margin.totals.tenants_red} red</span>
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-border/50 bg-card overflow-hidden" data-testid="tenants-table">
                  <div className="px-4 py-3 border-b border-border/50">
                    <h3 className="text-sm font-semibold">Per-Tenant Margin (sorted by worst ratio first)</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/50 border-b border-border/50">
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Tenant</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Plan · Seats</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Queries</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Cost</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Revenue</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Ratio</th>
                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Cost Drivers (LLM/Store/OCR)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(function() {
                          var rows = [];
                          if (!margin.tenants || margin.tenants.length === 0) {
                            rows.push(<tr key="empty"><td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">No tenants in window.</td></tr>);
                          } else {
                            for (var ti = 0; ti < margin.tenants.length; ti++) {
                              var t = margin.tenants[ti];
                              var sevStyle = t.severity === 'red'
                                ? 'bg-red-500/10 text-red-600 border-red-500/30'
                                : t.severity === 'yellow'
                                  ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
                                  : 'bg-green-500/10 text-green-600 border-green-500/30';
                              rows.push(
                                <tr key={ti} className="border-b border-border/30 hover:bg-muted/20" data-testid={'tenant-row-' + ti}>
                                  <td className="px-4 py-2 text-sm truncate max-w-[180px]" title={t.org_name}>{t.org_name}</td>
                                  <td className="px-4 py-2 text-xs">{t.plan_name} · <span className="tabular-nums">{t.seats}</span></td>
                                  <td className="px-4 py-2 text-xs tabular-nums">{t.queries.toLocaleString()}</td>
                                  <td className="px-4 py-2 text-xs tabular-nums">₹{t.cost_inr.toLocaleString()}</td>
                                  <td className="px-4 py-2 text-xs tabular-nums">₹{t.revenue_inr.toLocaleString()}</td>
                                  <td className="px-4 py-2 text-xs">
                                    <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold border ' + sevStyle}>
                                      {t.ratio_pct}%
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-[11px] tabular-nums text-muted-foreground">
                                    {t.cost_drivers_pct.llm}% / {t.cost_drivers_pct.storage}% / {t.cost_drivers_pct.ocr}%
                                  </td>
                                </tr>
                              );
                            }
                          }
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
